import {
    all,
    call,
    delay,
    put,
    select,
    spawn,
    take,
    takeEvery,
    takeLatest,
    takeLeading,
} from 'redux-saga/effects';

import * as storage from '@elab/core/utils/storage';
import { insertNode, removeNodes, replaceChildNode } from '@elab/core/utils/dom/manipulations';
import logger from '@elab/core/utils/logger';
import { selectors as configSelectors } from '@elab/core/store/config';
import { checkLoggedStatus } from '@elab/core/store/user/saga';
import { getUserId } from '@elab/core/utils/user/sagaHelpers';

import { actions as globalActions } from '../global';
import analytics from '../../utils/analytics';
import * as address from '../../utils/address';
import { globalTypes } from '../types';
import { actions as uiActions, selectors as uiSelectors } from '../ui';
import Breakpoints from '../../utils/Breakpoints';
import { history } from '../history';
import { types as userTypes } from '../user';

import { changeFiltersStrategyCreator, getStrategyName } from './strategies/changeFilters';
import { changeBeforeApplyFiltersStrategy } from './strategies/beforeApplyFilters';
import api from './api';
import actions from './actions';
import selectors from './selectors';
import types from './types';

export const changeFiltersStrategy = changeFiltersStrategyCreator(
    getFilteredResults,
    getNewDoc,
    updateDOMWithNewFilters,
    updateDOMWithNewProducts,
);

function* onInit() {
    yield put(actions.setValue(''));
}

function* watchInitilize() {
    yield takeEvery(globalTypes.INITIALIZE, onInit);
}

export function* checkIfAnySuggestionsReceived(activeList) {
    const status = Object.entries(yield select(selectors.getSearchResults))
        .filter(
            ([key, suggestionGroup]) => Array.isArray(suggestionGroup) && activeList.includes(key),
        )
        .some(([, suggestionGroup]) => suggestionGroup.length);
    const shouldShowNoResultsMessage = yield select(configSelectors.shouldShowNoResultsMessage);

    yield put(actions.setSuggestionsReceived(status || shouldShowNoResultsMessage));
}

export function* handleInputValueChange({ payload }) {
    const { value, minCharacters, activeSuggestions } = payload;

    if (value.length >= minCharacters) {
        yield call(searchResultsAfterTimeOut, payload);
        yield call(checkIfAnySuggestionsReceived, activeSuggestions);
    }
}

export function* searchResultsAfterTimeOut(payload) {
    try {
        yield delay(payload.waitTime);
        const result = yield call(api.getSearchResults, payload.value, payload.componentUid);
        const searchValue = yield select(selectors.getSearchValue);
        yield all([
            put(actions.setResultCategories(result.categories)),
            put(actions.setResultSuggestions(result.contentSuggestions)),
            put(
                actions.setResultProducts({
                    searchResults: result.products,
                    totalNumberOfResults: result.productsTotalNumberOfResults,
                }),
            ),
            put(actions.setResultStores(result.stores)),
            put(actions.setStaticSuggestion(result.staticSuggestion)),
            put(actions.setResultAutoSuggestions(result.suggestions)),
            put(actions.setBrandSuggestions(result.brands)),
            put(analytics.handleNoSearchResult(result.hasResults, searchValue)),
            put(
                actions.setHasExternalCatalogsMapping(
                    result.hasOwnProperty('externalCatalogsMapping'),
                ),
            ),
            put(actions.setNoResultsMessage(result.noResultsMessage)),
            put(actions.setHasResults(result.hasResults)),
            put(actions.setAPIResponseSucceded(true)),
            put(actions.setResultBaseUrlSuffix(result.baseUrlSuffix)),
            put(actions.setExternalCatalogsMapping(result.externalCatalogsMapping)),
        ]);
    } catch (e) {
        /* Nothing */
    }
}

export function* getSearchUrl({ term, url, queryKeys = ['q'], searchType }) {
    const urlObj = new URL(url, yield call(address.getOrigin));
    queryKeys.forEach(key => urlObj.searchParams.append(key, term));
    if (searchType) {
        urlObj.searchParams.append('searchType', searchType);
    }
    return urlObj.toString();
}

export function* doSearch({ payload }) {
    const { term, trackAnalytics } = payload;
    if (trackAnalytics) {
        yield call(analytics.handleSearch, term);
    }
    const redirectUrl = yield call(getSearchUrl, payload);
    yield put(globalActions.redirect(redirectUrl));
}

export function* getSecondaryResultsTotal({ payload }) {
    const term = yield select(selectors.getLastSearch);
    try {
        const total = yield call(api.getSearchResultsCounter, term, payload);
        yield put(actions.setSecondaryResultsTotal(total));
    } catch (error) {
        yield call(logger.error, error);
    }
}

function* watchDoSearch() {
    yield takeEvery(types.DO_SEARCH, doSearch);
}

export function* doFilterByValue({ payload }) {
    const { uri, uid } = payload;
    yield put(actions.changeFilter({ uri, uid, groupUid: uid }));
}

function* watchDoFilterByValue() {
    yield takeEvery(types.DO_FILTER_BY_VALUE, doFilterByValue);
}

export function getUpdatedRecentSearches(recentSearches, { value, maxRecentSearches }) {
    const uniqueSearches = recentSearches.filter(recentSearch => recentSearch.value !== value);
    const date = new Date();
    const expireDate = date.getTime() + 30 * 24 * 60 * 60 * 1000;
    date.setTime(expireDate);

    const previousRecentSearches = maxRecentSearches > 1 ? maxRecentSearches - 1 : 0;

    return [
        { value, expireDate: date.toUTCString() },
        ...uniqueSearches.slice(0, previousRecentSearches),
    ];
}

export function* addRecentSearch({ payload }) {
    const recentSearches = yield select(selectors.getRecentSearches);
    const updatedRecentSearches = yield call(getUpdatedRecentSearches, recentSearches, payload);

    yield call(storage.set, storage.key.recentSearches, JSON.stringify(updatedRecentSearches));
}

export function* getRecentSearch() {
    const recentSearches = yield call(storage.get, storage.key.recentSearches);

    if (!recentSearches) return;

    const validRecentSearches = JSON.parse(recentSearches).filter(
        ({ expireDate }) => new Date(expireDate).getTime() > new Date().getTime(),
    );

    yield put(actions.setRecentSearch(validRecentSearches));
}

function* watchAddRecentSearch() {
    yield takeEvery(types.ADD_RECENT_SEARCH, addRecentSearch);
}

export function* clearRecentSearches() {
    yield call(storage.set, storage.key.recentSearches, JSON.stringify([]));
    yield put(actions.setRecentSearch([]));
}

function* watchClearRecentSearch() {
    yield takeEvery(types.CLEAR_RECENT_SEARCHES, clearRecentSearches);
}

function* watchGetSearchPopularSuggestions() {
    yield takeEvery(types.GET_POPULAR_SUGGESTIONS, getSearchPopularSuggestions);
}

export function* getSearchPopularSuggestions({ payload }) {
    try {
        const popularSuggestions = yield call(
            api.getPopularSuggestions,
            payload.maxPopularSearches,
        );
        yield put(actions.setPopularSuggestions(popularSuggestions));
    } catch (err) {
        yield call(logger.error, err);
    }
}

export function* getConfigs() {
    let config = {};
    if (Breakpoints.mobileDevice) {
        config = yield select(configSelectors.getMobileFiltersConfig);
    } else {
        if (Breakpoints.onlyTabletDevice) {
            config = yield select(configSelectors.getTabletFiltersConfig);
        }
        if (Breakpoints.desktopDevice || Object.keys(config).length === 0) {
            config = yield select(configSelectors.getDesktopFiltersConfig);
        }
    }
    return config;
}

const domParser = new window.DOMParser();

export function* getNewDoc() {
    const newDocString = yield select(selectors.getNewDoc);

    if (!newDocString) {
        return;
    }
    return domParser.parseFromString(newDocString, 'text/html');
}

const getFilterClass = mod => `.plp-filters--${mod} .plp-filters__list`;
const getFilterById = id => `#${id} .plp-filters__list`;

export function* updateDOMWithNewFilters(newDoc) {
    if (!newDoc) {
        return;
    }

    const resetActiveAlphabetFilterStatus = yield select(
        configSelectors.getResetActiveAlphabetFilterStatus,
    );
    if (!resetActiveAlphabetFilterStatus) {
        yield put(uiActions.clearActiveAlphabetFilter());
    }
    yield call(setValidatedFilters);

    const desktopFiltersGroupId = yield select(configSelectors.getDesktopFiltersGroupId);
    const mobileFiltersGroupId = yield select(configSelectors.getMobileFiltersGroupId);

    const desktopFiltersGroup =
        document.querySelector(getFilterById(desktopFiltersGroupId)) ||
        document.querySelector(getFilterClass('search'));

    const mobileFiltersGroup =
        document.querySelector(getFilterById(desktopFiltersGroupId)) ||
        document.querySelector(getFilterClass('mobile'));

    const newFiltersGroup = newDoc.querySelector('.plp-filters__list');

    if (!mobileFiltersGroup || !desktopFiltersGroup || !newFiltersGroup) {
        return;
    }

    const position = {
        COINCIDES: 'coincides',
        OLD_FILTER_ABSENT: 'absent',
        OLD_FILTER_AHEAD: 'ahead',
    };

    const getPositionComparedByIndex = (currentIndex, newIndex) => {
        if (currentIndex === newIndex) {
            return position.COINCIDES;
        }

        if (currentIndex === -1) {
            return position.OLD_FILTER_ABSENT;
        }

        return position.OLD_FILTER_AHEAD;
    };

    const removeOldFiltersFromEnd = (currentFiltersGroup, currentFiltersList, newFiltersGroup) => {
        const currentFiltersCount = currentFiltersList.length;
        const newFiltersCount = newFiltersGroup.children.length;

        if (currentFiltersCount > newFiltersCount) {
            removeNodes(
                currentFiltersGroup,
                currentFiltersList,
                newFiltersCount,
                currentFiltersCount - 1,
            );
        }
    };

    const replaceOldFiltersGroupWithNew = currentFiltersGroup => {
        const newFiltersList = [...newFiltersGroup.cloneNode(true).children];
        const currentFiltersList = currentFiltersGroup.children;

        const getFilterUid = element =>
            element.dataset.uid || element.firstElementChild.dataset.uid;

        const replaceOldFiltersWithNewOneByOne = (newFilter, newFilterIndex) => {
            const currentFilterIndex = [...currentFiltersList].findIndex(
                f => getFilterUid(f) === getFilterUid(newFilter),
            );

            switch (getPositionComparedByIndex(currentFilterIndex, newFilterIndex)) {
                case position.COINCIDES: {
                    replaceChildNode(
                        currentFiltersGroup,
                        currentFiltersList[newFilterIndex],
                        newFilter,
                    );
                    break;
                }
                case position.OLD_FILTER_ABSENT: {
                    insertNode(currentFiltersList, newFilter, newFilterIndex);
                    break;
                }
                case position.OLD_FILTER_AHEAD: {
                    removeNodes(
                        currentFiltersGroup,
                        currentFiltersList,
                        newFilterIndex,
                        currentFilterIndex - 1,
                    );

                    const append = newFilterIndex >= currentFiltersList.length;

                    if (append) {
                        insertNode(currentFiltersList, newFilter, currentFiltersList.length);
                    } else {
                        replaceChildNode(
                            currentFiltersGroup,
                            currentFiltersList[newFilterIndex],
                            newFilter,
                        );
                    }
                    break;
                }
                // no default
            }
        };

        newFiltersList.forEach(replaceOldFiltersWithNewOneByOne);
        removeOldFiltersFromEnd(currentFiltersGroup, currentFiltersList, newFiltersGroup);
    };

    let firstGroupToReplace = desktopFiltersGroup;
    let delayedGroupToReplace = mobileFiltersGroup;
    const windowSize = yield select(uiSelectors.getWindowSize);
    if (Breakpoints.isMobile(windowSize.width)) {
        firstGroupToReplace = mobileFiltersGroup;
        delayedGroupToReplace = desktopFiltersGroup;
    }

    replaceOldFiltersGroupWithNew(firstGroupToReplace);
    if (mobileFiltersGroupId !== desktopFiltersGroupId) {
        // Run later DOM update of non visible block of filters (for current view port)
        // Example: for mobile view, desktop filters will be upgraded with delay
        yield spawn(function*() {
            yield delay(3000);
            replaceOldFiltersGroupWithNew(delayedGroupToReplace);
        });
    }
}

export function* updateDOMWithNewProducts(newDoc) {
    if (!newDoc) {
        return;
    }

    const uri = yield select(selectors.getNewUri);
    const currHistoryState = window.history.state;

    history.push(uri);
    yield call(address.replaceState, { state: currHistoryState });

    const productListWrapperId = yield select(configSelectors.getProductListWrapperId);
    const productListWrapper =
        document.getElementById(productListWrapperId) ||
        document.querySelector('.product__list--wrapper');

    const newProductListWrapper =
        newDoc.getElementById(productListWrapperId) ||
        newDoc.querySelector('.product__list--wrapper');

    if (productListWrapper && newProductListWrapper) {
        productListWrapper.innerHTML = newProductListWrapper.innerHTML;
    }

    const pageUpdateSelectorsList = yield select(configSelectors.getPageUpdateSelectorsList);
    const isRemoveSeo = yield select(selectors.getRemoveSeoText);
    pageUpdateSelectorsList.forEach(selector => {
        if (!selector) {
            return;
        }

        const currentElement = document.querySelector(selector);
        const newElement = newDoc.querySelector(selector);
        const currentElementSeoText = document.getElementById('seoContent');
        const newElementSeoText = newDoc.getElementById('seoContent');

        if (currentElement && newElement) {
            const pageParams = new URL(window.location).searchParams;
            const pageNumber = pageParams.get('page');
            if (currentElementSeoText) {
                if (pageNumber && isRemoveSeo) {
                    currentElementSeoText.innerHTML = '';
                } else if (isRemoveSeo) {
                    currentElementSeoText.innerHTML = newElementSeoText.innerHTML;
                }
            }
            currentElement.innerHTML = newElement.innerHTML;

            if (isRemoveSeo) {
                document.title = newDoc.title;
                document.querySelector('meta[name="description"]').content = newDoc.querySelector(
                    'meta[name="description"]',
                ).content;
            }
        }
    });

    yield put(actions.setNewDoc({ newDoc: null, newUri: null }));
    yield call(setValidatedFilters);
}

export function* getFilteredResults(uri) {
    try {
        yield put(actions.setFilterLoading(true));
        yield call(analytics.handleFacetCheck, uri);
        const response = yield call(api.getSearchFilteredResults, uri);
        yield put(actions.setNewDoc({ newDoc: response, newUri: uri }));
    } catch (e) {
        yield call(logger.error, e);
    } finally {
        yield put(actions.setFilterLoading(false));
    }
}

export function* changeFilter({ payload }) {
    const { uri, uid, forceUpdate, name } = payload;
    if (!uri && !name) return;
    let newUri;
    if (name) {
        newUri = yield call(getUrlWithoutFilters, name);
    } else {
        newUri = decodeURIComponent(uri)
            .replaceAll('%2B', '+')
            .replaceAll('+', ' ');
    }

    const config = yield call(getConfigs);

    if (config.scrollToTop) {
        yield put(
            uiActions.scrollToTop({
                target: 'e2-plp-filter-paginator',
                shift: 10,
            }),
        );
    }

    const strategyName = getStrategyName(config, forceUpdate);
    yield call(changeFiltersStrategy[strategyName], newUri);

    if (config.scrollToAppliedFilter) {
        yield put(uiActions.scrollToAppliedFilter(uid));
    }
}

function* watchChangeFilter() {
    yield takeLatest(types.CHANGE_FILTER, changeFilter);
}

export function* applyFilters({ payload }) {
    const strategy = yield select(configSelectors.getBeforeApplyFiltersConfig);
    yield call(changeBeforeApplyFiltersStrategy[strategy], payload);
    const newDoc = yield call(getNewDoc);
    yield call(updateDOMWithNewProducts, newDoc);
    yield call(setValidatedFilters);
    yield delay(30);
    yield put(uiActions.scrollToTop({ target: 'e2-plp-filter-paginator', shift: 10 }));
}

function* watchApplyFilters() {
    yield takeEvery(types.APPLY_FILTERS, applyFilters);
}

export function* getUrlWithoutFilters(filtersName) {
    const uri = yield select(selectors.getNewUri);
    let fullUrl;

    if (uri) {
        fullUrl = new URL(uri, yield call(address.getOrigin));
    } else {
        fullUrl = new URL(yield call(address.getURL));
    }
    const fullQuery = fullUrl.searchParams.get('q');

    if (fullQuery) {
        const filtersParams = fullQuery.split(':');
        let newSearchQuery;

        if (filtersName) {
            newSearchQuery = filtersParams
                .filter((item, i, arr) => filtersName !== item && filtersName !== arr[i - 1])
                .filter(data => data === 0 || Boolean(data))
                .join(':');
        } else {
            newSearchQuery = filtersParams.shift();
        }
        fullUrl.searchParams.set('q', newSearchQuery);
    }

    return fullUrl.pathname + fullUrl.search;
}

export function* clearFilters({ payload }) {
    const config = yield call(getConfigs);

    if (!config.resetAfterApply) {
        const strategy = yield select(configSelectors.getBeforeApplyFiltersConfig);
        yield call(changeBeforeApplyFiltersStrategy[strategy], payload);
        yield put(uiActions.scrollToTop({ target: 'e2-plp-filter-paginator', shift: 10 }));
    }

    const isBeforeValidate = payload.hasOwnProperty('beforeValidate');
    let newUrl;
    if (isBeforeValidate) {
        newUrl = yield call(address.getURL);
    } else {
        newUrl = yield call(getUrlWithoutFilters);
    }
    yield call(getFilteredResults, newUrl);
    const newDoc = yield call(getNewDoc);
    yield call(updateDOMWithNewFilters, newDoc);

    if (!isBeforeValidate && !config.resetAfterApply) {
        yield call(updateDOMWithNewProducts, newDoc);
    }
}

export function* clearFiltersBeforeRender() {
    yield put(actions.setNewDoc({ newUri: null, newDoc: null }));
    yield put(actions.clearLastChangedFiltersGroup());
    yield put(uiActions.clearActiveAlphabetFilter());
}

function* watchClearFiltersBeforeRender() {
    yield takeEvery(types.CLEAR_FILTERS_BEFORE_RENDER, clearFiltersBeforeRender);
}

function* watchClearFilters() {
    yield takeEvery(types.CLEAR_FILTERS, clearFilters);
}

export function* clearGroupOfFilters({ payload }) {
    const newUri = yield call(getUrlWithoutFilters, payload.name);
    yield call(getFilteredResults, newUri);
    const newDoc = yield call(getNewDoc);
    yield call(updateDOMWithNewFilters, newDoc);
    yield call(updateDOMWithNewProducts, newDoc);
}

function* watchClearGroupOfFilters() {
    yield takeEvery(types.CLEAR_GROUP_OF_FILTERS, clearGroupOfFilters);
}

export function* changeSorting({ payload }) {
    const { uri, accordionName, accordionGroup } = payload;
    yield put(uiActions.scrollToTop({ target: 'e2-plp-filter-paginator', shift: 10 }));
    yield call(getFilteredResults, uri);
    const newDoc = yield call(getNewDoc);
    yield call(updateDOMWithNewFilters, newDoc);
    yield call(updateDOMWithNewProducts, newDoc);
    if (accordionName && accordionGroup) {
        yield put(
            uiActions.accordionClose({
                group: accordionGroup,
                name: accordionName,
            }),
        );
    }
}

function* watchChangeSorting() {
    yield takeEvery(types.CHANGE_SORTING, changeSorting);
}

export function* isActiveFilters() {
    const query = new URL(yield call(address.getURL)).searchParams.get('q') || '';
    return query.split(':').length > 2;
}

export function* setValidatedFilters() {
    let isValidatedFilters = true;
    const newUri = yield select(selectors.getNewUri);
    if (newUri) {
        const query = new URL(yield call(address.getURL)).searchParams.get('q') || '';
        const newQuery = new URL(newUri, yield call(address.getOrigin)).searchParams.get('q') || '';

        isValidatedFilters = query === newQuery;
    }

    yield put(actions.setFiltersValidated(isValidatedFilters));
}

export function* checkUrlForParams() {
    const newUri = yield select(selectors.getNewUri);
    const hasActiveFilters = yield call(isActiveFilters);
    yield put(actions.setFiltersAreApplied(hasActiveFilters || !!newUri));
}

function* watchChangeUrl() {
    yield takeEvery([globalTypes.INITIALIZE, types.SET_NEW_DOC], checkUrlForParams);
}

function* watchFilterLoading({ payload }) {
    const filterLoadingModifier = yield select(configSelectors.getFilterLoadingModifier);

    if (filterLoadingModifier) {
        document.body.classList.toggle(filterLoadingModifier, payload);
    }
}

export function* watchUpdatePageByUrl({ payload }) {
    yield call(getFilteredResults, payload);
    const newDoc = yield call(getNewDoc);
    yield call(updateDOMWithNewFilters, newDoc);
    yield call(updateDOMWithNewProducts, newDoc);
}

export function* searchProduct(action) {
    const isLogged = yield call(checkLoggedStatus);
    const { source, _query: query, pageNumber, eventType } = action.payload;
    if (isLogged) {
        let userId = yield call(getUserId);

        if (!userId) {
            yield take(userTypes.SET_PROFILE);
            userId = yield select(selectors.getUserId);
        }

        const _pageNumber = pageNumber || 0;

        try {
            const data = yield call(api.getSearchProduct, source, query, _pageNumber);
            if (Object.keys(data).length > 0) {
                const result = {};
                result[eventType] = data;
                yield put(actions.setSearchForExtService(result));
            }

            const search = yield select(selectors.getSearchForExtService, eventType);
            const event = new CustomEvent(eventType, { detail: search });
            window.dispatchEvent(event);
        } catch (e) {
            yield call(logger.error, e);
            window.dispatchEvent(new CustomEvent(`${eventType}Error`, { detail: e }));
        }
    }
}
export function* searchResultPageData({ payload }) {
    yield spawn(analytics.handleViewSearchResultsEvent, {
        searchQuantity: payload.results,
        searchValue: payload.term,
    });
}

export default [
    watchInitilize(),
    takeLatest(types.SET_INPUT_VALUE, handleInputValueChange),
    watchDoSearch(),
    watchChangeFilter(),
    watchApplyFilters(),
    watchClearFilters(),
    watchClearGroupOfFilters(),
    watchChangeSorting(),
    watchChangeUrl(),
    watchAddRecentSearch(),
    watchClearRecentSearch(),
    watchDoFilterByValue(),
    watchClearFiltersBeforeRender(),
    watchGetSearchPopularSuggestions(),
    takeLeading(types.GET_SECONDARY_RESULTS_TOTAL, getSecondaryResultsTotal),
    takeLeading(types.GET_RECENT_SEARCH, getRecentSearch),
    takeLeading(types.FILTER_LOADING, watchFilterLoading),
    takeLeading(types.UPDATE_PAGE_BY_URL, watchUpdatePageByUrl),
    takeEvery(types.SEARCH_PRODUCT, searchProduct),
    takeEvery(types.SEARCH_RESULTS_PAGE_INFO, searchResultPageData),
];
